/**
 * Recipe Ingredients Cache Store
 * Caches recipe ingredients for quick modal loading
 * Features:
 * - Prefetch on visible for instant modal open
 * - FIFO eviction when cache exceeds max size
 * - Deduplicates in-flight requests
 */

import { create } from 'zustand';
import { fetchRecipeIngredients } from '@/lib/api/recipeIngredients';
import type { RecipeIngredient } from '@/types/api';

// Constants
const DEFAULT_CACHE_SIZE = 10;

interface CacheEntry {
  recipeId: number;
  ingredients: RecipeIngredient[];
  timestamp: number;
}

interface RecipeIngredientsCacheState {
  entries: CacheEntry[];
  pendingFetches: Set<number>;
  maxSize: number;

  // Get cached ingredients (returns undefined if not cached)
  get: (recipeId: number) => RecipeIngredient[] | undefined;

  // Prefetch ingredients (called when card becomes visible)
  prefetch: (recipeId: number) => Promise<void>;

  // Set cache entry (used after successful fetch)
  set: (recipeId: number, ingredients: RecipeIngredient[]) => void;

  // Invalidate cache for a recipe (after modifications)
  invalidate: (recipeId: number) => void;
}

export const useRecipeIngredientsCacheStore = create<RecipeIngredientsCacheState>(
  (set, get) => ({
    entries: [],
    pendingFetches: new Set(),
    maxSize: DEFAULT_CACHE_SIZE,

    get: (recipeId: number) => {
      const entry = get().entries.find((e) => e.recipeId === recipeId);
      return entry?.ingredients;
    },

    prefetch: async (recipeId: number) => {
      const state = get();

      // Skip if already cached or fetch in progress
      if (state.get(recipeId) !== undefined) return;
      if (state.pendingFetches.has(recipeId)) return;

      // Mark as pending
      set((s) => ({
        pendingFetches: new Set([...s.pendingFetches, recipeId]),
      }));

      try {
        const ingredients = await fetchRecipeIngredients(recipeId);
        get().set(recipeId, ingredients);
      } catch (error) {
        // Log in development for debugging, but don't crash - will retry on modal open
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Prefetch failed for recipe ${recipeId}:`, error);
        }
      } finally {
        // Remove from pending
        set((s) => {
          const newPending = new Set(s.pendingFetches);
          newPending.delete(recipeId);
          return { pendingFetches: newPending };
        });
      }
    },

    set: (recipeId: number, ingredients: RecipeIngredient[]) => {
      set((state) => {
        // Remove existing entry for this recipe if any
        let newEntries = state.entries.filter((e) => e.recipeId !== recipeId);

        // Add new entry
        newEntries.push({
          recipeId,
          ingredients,
          timestamp: Date.now(),
        });

        // FIFO eviction: remove oldest entries if over limit
        if (newEntries.length > state.maxSize) {
          // Sort by timestamp (oldest first) and remove excess
          newEntries.sort((a, b) => a.timestamp - b.timestamp);
          newEntries = newEntries.slice(newEntries.length - state.maxSize);
        }

        return { entries: newEntries };
      });
    },

    invalidate: (recipeId: number) => {
      set((state) => ({
        entries: state.entries.filter((e) => e.recipeId !== recipeId),
      }));
    },
  })
);
